import { Address, Tenant, ManagedInvestment, SalesUnit } from '../types/management'
import { supabase } from '../supabaseClient'

// Mock database for initial state
export const INITIAL_TENANTS: Tenant[] = [
  {
    id: '1',
    name: 'Budex Development Sp. z o.o.',
    slug: 'budex-development',
    nip: '1234567890',
    krs: '0000123456',
    regon: '142345678',
    address: {
      street: 'Prosta',
      buildingNumber: '5',
      flatNumber: '12',
      city: 'Warszawa',
      zipCode: '00-001',
      voivodeship: 'Mazowieckie',
      district: 'Warszawa',
      community: 'Wola'
    },
    contact: {
      email: 'biuro@budex.pl',
      phone: '+48 22 555 00 99',
      website: 'www.budex.pl'
    },
    createdAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Dom Marzen S.A.',
    slug: 'dom-marzen',
    nip: '9876543210',
    krs: '0000987654',
    regon: '987123456',
    address: {
      street: 'Krzywa',
      buildingNumber: '12',
      city: 'Krakow',
      zipCode: '30-002',
      voivodeship: 'Malopolskie',
      district: 'Krakow',
      community: 'Krakow-Srodmiescie'
    },
    createdAt: new Date().toISOString()
  }
]

export const INITIAL_INVESTMENTS: ManagedInvestment[] = [
  {
    id: 'inv-1',
    tenantId: '1', // Budex Development
    name: 'Osiedle Zielone Ogrody',
    slug: 'osiedle-zielone-ogrody',
    status: 'ACTIVE',
    salesStartDate: '2023-01-01',
    stages: [
      { id: 'stage-1', name: 'Etap I - Budynki A, B' },
      { id: 'stage-2', name: 'Etap II - Budynki C, D' },
      { id: 'stage-3', name: 'Etap III - Penthouse' }
    ],
    investmentAddress: {
      street: 'Lesna',
      buildingNumber: '15',
      city: 'Warszawa',
      zipCode: '02-345',
      voivodeship: 'Mazowieckie',
      district: 'Warszawa',
      community: 'Bemowo'
    },
    salesOfficeAddress: {
      street: 'Prosta',
      buildingNumber: '5',
      flatNumber: '12',
      city: 'Warszawa',
      zipCode: '00-001',
      voivodeship: 'Mazowieckie'
    },
    contact: {
      website: 'www.zielone-ogrody.pl',
      email: 'biuro@budex.pl',
      phone: '+48 22 555 00 99'
    }
  }
]

const templateId1 = 'tpl-1'

export const INITIAL_UNITS: SalesUnit[] = [
  {
    id: 'unit-1',
    investmentId: 'inv-1',
    stageId: 'stage-1',
    type: 'APARTMENT',
    status: 'AVAILABLE',
    number: 'A.0.1',
    area: 45.5,
    basePrice: 455000,
    additionalComponents: [],
    history: []
  },
  {
    id: 'unit-2',
    investmentId: 'inv-1',
    stageId: 'stage-1',
    type: 'APARTMENT',
    status: 'RESERVED',
    number: 'A.0.2',
    area: 60.0,
    basePrice: 580000,
    additionalComponents: [
      { id: 'comp-1', name: 'Miejsce postojowe', price: 35000 }
    ],
    history: []
  },
  // Bulk created units example
  {
    id: 'unit-3',
    investmentId: 'inv-1',
    stageId: 'stage-2',
    templateId: templateId1,
    type: 'APARTMENT',
    status: 'PLANNING',
    number: '1A',
    area: 52.0,
    basePrice: 520000,
    additionalComponents: [],
    history: []
  },
  {
    id: 'unit-4',
    investmentId: 'inv-1',
    stageId: 'stage-2',
    templateId: templateId1,
    type: 'APARTMENT',
    status: 'PLANNING',
    number: '2A',
    area: 52.0,
    basePrice: 520000,
    additionalComponents: [],
    history: []
  },
  {
    id: 'unit-5',
    investmentId: 'inv-1',
    stageId: 'stage-2',
    templateId: templateId1,
    type: 'APARTMENT',
    status: 'PLANNING',
    number: '3A',
    area: 52.0,
    basePrice: 520000,
    additionalComponents: [],
    history: []
  }
]

// NIP resolution service via Supabase edge function (REGON API)
export const resolveNip = async (nip: string): Promise<Partial<Tenant> | null> => {
  // Validate NIP format
  if (!/^\d{10}$/.test(nip)) {
    return null
  }

  try {
    const { data: sessionData } = await supabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token

    const response = await fetch(
      `https://asfnygqjjpsvqdlpqzso.supabase.co/functions/v1/regon?nip=${nip}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      console.error('REGON API error:', response.status, response.statusText)
      return null
    }

    const data = await response.json()

    if (!data || data.error) {
      console.error('REGON API returned error:', data?.error)
      return null
    }

    // Map REGON API response (bir1 modern normalize) to Tenant format
    // Field names from API: nazwa, regon, nip, ulica, nrNieruchomosci, nrLokalu,
    // miejscowosc, kodPocztowy, wojewodztwo, powiat, gmina, numerWrejestrzeEwidencji (KRS)
    return {
      name: data.nazwa || '',
      krs: data.numerWrejestrzeEwidencji || '', // Note: lowercase 'r' in 'rejestrze'
      regon: data.regon || '',
      address: {
        street: data.ulica || '',
        buildingNumber: data.nrNieruchomosci || '',
        flatNumber: data.nrLokalu || undefined,
        city: data.miejscowosc || '',
        zipCode: data.kodPocztowy || '',
        voivodeship: data.wojewodztwo || '',
        district: data.powiat || '',
        community: data.gmina || ''
      }
    }
  } catch (error) {
    console.error('Error resolving NIP:', error)
    return null
  }
}

// Address autocomplete using OpenStreetMap Nominatim API
// Free API, 1 request/second limit, Poland-focused search
export const searchAddress = async (query: string): Promise<Address[]> => {
  if (query.length < 3) {
    return []
  }

  try {
    // Search only in Poland, limit to 5 results
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `q=${encodeURIComponent(query)}&` +
      `countrycodes=pl&` +
      `format=json&` +
      `addressdetails=1&` +
      `limit=5`,
      {
        headers: {
          'Accept-Language': 'pl',
          'User-Agent': 'TwoPointPanel/1.0' // Required by Nominatim
        }
      }
    )

    if (!response.ok) {
      console.error('Nominatim API error:', response.status)
      return []
    }

    const data = await response.json()

    // Map Nominatim response to our Address format
    return data.map((item: any) => {
      const addr = item.address || {}

      // Extract street and building number from display_name or address components
      let street = addr.road || addr.street || ''
      let buildingNumber = addr.house_number || ''

      // If no street, try to extract from display_name
      if (!street && item.display_name) {
        const parts = item.display_name.split(',')
        if (parts.length > 0) {
          const firstPart = parts[0].trim()
          // Check if first part contains a number (likely street + number)
          const match = firstPart.match(/^(.+?)\s+(\d+\w*)$/)
          if (match) {
            street = match[1]
            buildingNumber = match[2]
          } else {
            street = firstPart
          }
        }
      }

      return {
        street,
        buildingNumber,
        flatNumber: undefined,
        city: addr.city || addr.town || addr.village || addr.municipality || '',
        zipCode: addr.postcode || '',
        voivodeship: addr.state || '',
        district: addr.county || '',
        community: addr.municipality || addr.city_district || ''
      } as Address
    }).filter((addr: Address) => addr.city || addr.street) // Filter out empty results
  } catch (error) {
    console.error('Error searching address:', error)
    return []
  }
}

// Mock AI parsing service
export interface ParsedUnitData {
  number?: string
  area?: number
  basePrice?: number
  additionalComponents?: { name: string; price: number }[]
}

export const parseUnitNaturalLanguage = async (text: string): Promise<ParsedUnitData> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const result: ParsedUnitData = { additionalComponents: [] }
      const lower = text.toLowerCase()

      // Mock logic: Extract area (e.g., "50m", "50 m2", "50 metrow")
      const areaMatch = text.match(/(\d+(?:[.,]\d+)?)\s*(?:m2|m|metr)/i)
      if (areaMatch) {
        result.area = parseFloat(areaMatch[1].replace(',', '.'))
      }

      // Mock logic: Extract prices. Assuming the largest number > 1000 is base price
      const numbers = text.match(/(\d[\d\s]*\d)/g)?.map(s => parseInt(s.replace(/\s/g, ''))) || []
      const potentialPrices = numbers.filter(n => n > 10000).sort((a, b) => b - a)

      if (potentialPrices.length > 0) {
        result.basePrice = potentialPrices[0]
      }

      // Mock logic: Extract ID/Number (e.g., "M4", "lokal 5")
      const idMatch = text.match(/(?:mieszkanie|lokal|nr)\s*([a-zA-Z0-9\.-]+)/i)
      if (idMatch) {
        result.number = idMatch[1]
      } else {
        // Fallback: try to find first short token that looks like an ID if not found above
        const tokens = text.split(' ')
        const likelyId = tokens.find(t => /^[A-Z]?\d+[A-Z]?$/.test(t))
        if (likelyId) result.number = likelyId
      }

      // Mock logic: Detect Garage/Parking
      if (lower.includes('garaz') || lower.includes('miejsce') || lower.includes('parking')) {
        // Try to find a smaller price for it
        const componentPrice = potentialPrices.length > 1 ? potentialPrices[1] : 30000 // Default mock price
        result.additionalComponents?.push({
          name: 'Miejsce postojowe',
          price: componentPrice
        })
      }
       // Mock logic: Komorka lokatorska
       if (lower.includes('komorka') || lower.includes('piwnica')) {
        result.additionalComponents?.push({
          name: 'Komorka lokatorska',
          price: 10000
        })
      }

      resolve(result)
    }, 1500) // Simulate network/LLM latency
  })
}
